import React from "react";
import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import IntersectGrid, { Backdrop } from "@/components/IntersectMotif";
import { FinalCTA } from "@/pages/Home";
import { IMG, SAUDI_CENTERS } from "@/lib/content";
import { useLang } from "@/lib/i18n";

const FOCUS = ["Economic transformation", "Investment environment", "New industries", "Technology", "Infrastructure", "Industrial development", "Tourism", "Entertainment", "Capital formation", "Entrepreneurship", "International participation"];
const SECTIONS = [
  { t: "Saudi Opportunity", b: "New industries, investment platforms and commercial ecosystems are being created across the Kingdom, opening opportunities for investment, partnership and venture building." },
  { t: "Emerging Industries", b: "From technology and financial services to tourism, entertainment and advanced manufacturing, entire sectors are being formed." },
  { t: "Market Entry Through Partnership", b: "For international companies, the most effective route into Saudi Arabia is often through the right local partner and structure." },
  { t: "Building Local Relationships", b: "Relationships with investors, founders, corporates and institutions are essential to creating durable value in the Kingdom." },
  { t: "Long-Term Participation", b: "Intersect seeks long-term participation in the Saudi economy — building companies rather than transactions." },
];

const SaudiMap = () => {
  const { t } = useLang();
  return (
    <div className="relative aspect-[4/3] max-w-2xl mx-auto">
      <IntersectGrid cols={6} rows={4} />
      <svg viewBox="0 0 100 80" className="w-full h-full">
        <path d="M18,20 L40,14 L62,18 L82,30 L80,48 L66,62 L44,66 L26,58 L16,40 Z" fill="none" stroke="#C6A15B" strokeOpacity="0.35" strokeWidth="0.4" />
      </svg>
      {SAUDI_CENTERS.map((c, i) => (
        <div key={c.name} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${c.x}%`, top: `${c.y}%` }}>
          <motion.span className="block w-2 h-2 rounded-full bg-primary-gold" animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.3 }} />
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] tracking-wide uppercase text-muted-text whitespace-nowrap">{t(c.name)}</span>
        </div>
      ))}
    </div>
  );
};

export default function SaudiArabia() {
  const { t } = useLang();
  return (
    <>
      <PageHero eyebrow="Saudi Arabia" title="Saudi Arabia is becoming a platform for global growth." image={IMG.cityNight}
        subtitle="One of the world's most ambitious economic transformations — and a growing intersection of global markets." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.kafdAerial} opacity={0.1} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-8">{t("Areas of Focus")}</p></Reveal>
          <div className="flex flex-wrap gap-3">
            {FOCUS.map((f, i) => (
              <Reveal key={f} delay={0.02 * i}>
                <span className="inline-block border border-primary-gold/20 px-5 py-2.5 text-sm text-soft-stone hover:border-primary-gold hover:text-primary-gold transition-colors" data-testid={`focus-${i}`}>{t(f)}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-charcoal relative overflow-hidden">
        <Backdrop src={IMG.kingdomDay} opacity={0.1} />
        <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal><p className="eyebrow mb-6">{t("Economic & Opportunity Centers")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("A Kingdom of expanding centers.")}</h2></Reveal>
            <Reveal delay={0.2}><p className="mt-8 text-lg leading-relaxed text-soft-stone max-w-lg">{t("Economic activity is expanding across multiple regions of Saudi Arabia, each developing its own industries and opportunities.")}</p></Reveal>
          </div>
          <div className="lg:col-span-6"><Reveal delay={0.2}><SaudiMap /></Reveal></div>
        </div>
      </section>

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.skylineSunset} opacity={0.09} />
        <div className="container-x relative z-10">
          <div className="divide-y divide-white/5 border-t border-white/5">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12" data-testid={`saudi-section-${i}`}>
                  <div className="md:col-span-1 text-primary-gold text-sm tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="md:col-span-4 text-2xl font-light tracking-tight text-ivory">{t(s.t)}</h3>
                  <p className="md:col-span-6 md:col-start-7 text-base leading-relaxed text-muted-text">{t(s.b)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-charcoal overflow-hidden relative">
        <Backdrop src={IMG.jeddahWater} opacity={0.1} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-6 text-center">{t("Global Connection")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory text-center max-w-3xl mx-auto">{t("Saudi Arabia at the intersection of global markets.")}</h2></Reveal>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 text-sm tracking-wide uppercase text-muted-text">
            {["North America", "Europe", "Asia", "Middle East", "Africa"].map((r) => (
              <span key={r} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-primary-gold" />{t(r)}</span>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
